import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import UMEventAwaitingList from "../../features/university-manager/UMEventAwaitingList";
import EventDetailModal from "../../components/common/EventDetailModal";
import PermissionGuard from "../../components/common/PermissionGuard";
import { useTenant } from "../../context/TenantContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import umService from "../../services/university-admin/umService";
import { useNotification } from "../../context/NotificationContext";

const UMAwaitingApproval = () => {
  const { t } = useTranslation("universityManager");
  const { can } = useUniAdminAuth();
  const { userRole, currentTenantId } = useTenant();
  const { showSuccess, showError } = useNotification();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [viewingEvent, setViewingEvent] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const res = await umService.getEvents({ userRole, currentTenantId, eventStatus: "PENDING", size: 50 });
    if (res.success) {
      setEvents(res.data.content);
      setTotalElements(res.data.totalElements);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, currentTenantId, refreshTrigger]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleApprove = async (eventId) => {
    try {
      const res = await umService.approveEvent(eventId);
      if (res.success) {
        showSuccess(t("events.awaitingApproval.approveSuccess"));
        setRefreshTrigger((v) => v + 1);
      } else {
        showError(t("events.awaitingApproval.approveFailed"));
      }
    } catch (error) {
      showError(t("events.awaitingApproval.approveFailed"));
    }
  };

  const handleReject = async (eventId, reason) => {
    try {
      const res = await umService.rejectEvent(eventId, reason);
      if (res.success) {
        showSuccess(t("events.awaitingApproval.rejectSuccess"));
        setRefreshTrigger((v) => v + 1);
      } else {
        showError(t("events.awaitingApproval.rejectFailed"));
      }
    } catch (error) {
      showError(t("events.awaitingApproval.rejectFailed"));
    }
  };

  return (
    <UniversityManagerLayout>
      <PermissionGuard allow={can(PERMISSIONS.event.list)}>
      <div className="awaiting-approval-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">
              <i className="fas fa-clock me-2 text-warning"></i>
              {t("events.awaitingApproval.title")}
            </h1>
            <p className="text-muted mb-0">{t("events.awaitingApproval.subtitle")}</p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => setRefreshTrigger((v) => v + 1)}
            disabled={loading}
          >
            <i className="fas fa-sync-alt me-2"></i>
            {t("events.awaitingApproval.refresh")}
          </button>
        </div>
        <UMEventAwaitingList
          events={events}
          loading={loading}
          totalElements={totalElements}
          onApprove={handleApprove}
          onReject={handleReject}
          onView={async (event) => {
            try {
              setLoading(true);
              const response = await umService.getEventById(event.id);
              if (response.success) {
                setViewingEvent(response.data);
              } else {
                showError(t("events.awaitingApproval.fetchFailed", { defaultValue: "Failed to fetch event details." }));
                setViewingEvent(event); // fallback to list item
              }
            } catch (error) {
              console.error("Error fetching event details:", error);
              showError(t("events.awaitingApproval.fetchFailed", { defaultValue: "Failed to fetch event details." }));
              setViewingEvent(event); // fallback to list item
            } finally {
              setLoading(false);
            }
          }}
        />
      </div>
      </PermissionGuard>

      {viewingEvent && (
        <EventDetailModal
          event={viewingEvent}
          onClose={() => setViewingEvent(null)}
        />
      )}
    </UniversityManagerLayout>
  );
};

export default UMAwaitingApproval;
